import { 
  collection, 
  doc, 
  addDoc, 
  deleteDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase/config';

/**
 * Service de gestion des notifications Firestore
 * Gère les notifications persistantes pour chaque utilisateur
 */
class NotificationService {
  constructor() {
    this.collectionName = 'notifications';
  }

  /**
   * Ajouter une nouvelle notification
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} notificationData - Données de la notification
   * @param {string} notificationData.message - Message de la notification
   * @param {string} notificationData.type - Type ('success' ou 'error')
   * @param {string} notificationData.category - Catégorie (optionnel: 'transcription', 'summary', 'system')
   * @param {Object} notificationData.metadata - Métadonnées additionnelles (optionnel)
   * @returns {Promise<string>} ID de la notification créée
   */
  async addNotification(userId, notificationData) {
    try {
      const notification = {
        userId,
        message: notificationData.message,
        type: notificationData.type || 'success',
        category: notificationData.category || 'system',
        metadata: notificationData.metadata || {},
        isRead: false,
        deleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.collectionName), notification);
      return docRef.id;
    } catch (error) {

      throw new Error('Impossible d\'ajouter la notification');
    }
  }

  /**
   * Supprimer une notification
   * @param {string} notificationId - ID de la notification à supprimer
   * @returns {Promise<void>}
   */
  async removeNotification(notificationId) {
    try {
      await deleteDoc(doc(db, this.collectionName, notificationId));
    } catch (error) {

      throw new Error('Impossible de supprimer la notification');
    }
  }

  /**
   * Marquer une notification comme lue
   * @param {string} notificationId - ID de la notification
   * @returns {Promise<void>}
   */
  async markAsRead(notificationId) {
    try {
      await updateDoc(doc(db, this.collectionName, notificationId), {
        isRead: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {

      throw new Error('Impossible de marquer la notification comme lue');
    }
  }

  /**
   * Marquer une notification comme supprimée (soft delete)
   * @param {string} notificationId - ID de la notification
   * @returns {Promise<void>}
   */
  async deleteNotification(notificationId) {
    try {
      await updateDoc(doc(db, this.collectionName, notificationId), {
        deleted: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {

      throw new Error('Impossible de supprimer la notification');
    }
  }

  /**
   * Marquer toutes les notifications d'un utilisateur comme lues
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<void>}
   */
  async markAllAsRead(userId) {
    try {
      // Note: Firestore ne permet pas les requêtes batch sur les queries
      // On va récupérer d'abord les notifications non lues puis les mettre à jour une par une
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('isRead', '==', false)
      );

      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(q, async (snapshot) => {
          try {
            const updatePromises = snapshot.docs.map(docSnapshot => 
              updateDoc(docSnapshot.ref, {
                isRead: true,
                updatedAt: serverTimestamp()
              })
            );
            
            await Promise.all(updatePromises);
            unsubscribe();
            resolve();
          } catch (error) {
            unsubscribe();
            reject(error);
          }
        }, reject);
      });
    } catch (error) {

      throw new Error('Impossible de marquer toutes les notifications comme lues');
    }
  }

  /**
   * Supprimer toutes les notifications d'un utilisateur (soft delete)
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<void>}
   */
  async deleteAllNotifications(userId) {
    try {
      // Récupérer toutes les notifications non supprimées de l'utilisateur
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('deleted', '==', false)
      );

      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(q, async (snapshot) => {
          try {
            const updatePromises = snapshot.docs.map(docSnapshot => 
              updateDoc(docSnapshot.ref, {
                deleted: true,
                updatedAt: serverTimestamp()
              })
            );
            
            await Promise.all(updatePromises);
            unsubscribe();
            resolve();
          } catch (error) {
            unsubscribe();
            reject(error);
          }
        }, reject);
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de toutes les notifications:', error);
      throw new Error('Impossible de supprimer toutes les notifications');
    }
  }

  /**
   * Écouter les notifications d'un utilisateur en temps réel
   * @param {string} userId - ID de l'utilisateur
   * @param {Function} callback - Fonction callback appelée lors des changements
   * @returns {Function} Fonction de désabonnement
   */
  subscribeToNotifications(userId, callback) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const notifications = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Convertir les timestamps en dates JavaScript
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate()
          }))
          // Filtrer les notifications supprimées
          .filter(notification => !notification.deleted);
        
        callback(notifications);
      }, (error) => {

        callback([]);
      });
    } catch (error) {

      return () => {}; // Retourner une fonction vide pour éviter les erreurs
    }
  }

  /**
   * Méthodes utilitaires pour créer des notifications spécifiques
   */
  
  // Notification de succès de transcription
  async addTranscriptionSuccessNotification(userId, meetingTitle, meetingId = null) {
    return this.addNotification(userId, {
      message: `Transcription terminée avec succès pour "${meetingTitle}"`,
      type: 'success',
      category: 'transcription',
      metadata: { meetingTitle, meetingId }
    });
  }

  // Notification d'erreur de transcription
  async addTranscriptionErrorNotification(userId, meetingTitle, errorMessage, meetingId = null) {
    return this.addNotification(userId, {
      message: `Erreur lors de la transcription de "${meetingTitle}": ${errorMessage}`,
      type: 'error',
      category: 'transcription',
      metadata: { meetingTitle, errorMessage, meetingId }
    });
  }

  // Notification de succès de résumé
  async addSummarySuccessNotification(userId, meetingTitle, meetingId = null) {
    return this.addNotification(userId, {
      message: `Résumé généré avec succès pour "${meetingTitle}"`,
      type: 'success',
      category: 'summary',
      metadata: { meetingTitle, meetingId }
    });
  }

  // Notification d'erreur de résumé
  async addSummaryErrorNotification(userId, meetingTitle, errorMessage, meetingId = null) {
    return this.addNotification(userId, {
      message: `Erreur lors de la génération du résumé de "${meetingTitle}": ${errorMessage}`,
      type: 'error',
      category: 'summary',
      metadata: { meetingTitle, errorMessage, meetingId }
    });
  }

  /**
   * Notification système générale
   */
  async addSystemNotification(userId, message, type = 'success') {
    return this.addNotification(userId, {
      message,
      type,
      category: 'system',
      metadata: {}
    });
  }

  /**
   * Notifications pour les actions spécifiques avec icônes
   */
  
  // Notification de copie
  async addCopyNotification(userId, contentType = 'contenu') {
    return this.addNotification(userId, {
      message: `📋 ${contentType} copié dans le presse-papier`,
      type: 'success',
      category: 'action',
      metadata: { action: 'copy', contentType }
    });
  }

  // Notification d'export
  async addExportNotification(userId, fileName, format = 'Word') {
    return this.addNotification(userId, {
      message: `📄 Export ${format} généré : ${fileName}`,
      type: 'success',
      category: 'action',
      metadata: { action: 'export', fileName, format }
    });
  }

  // Notification d'envoi par email
  async addEmailNotification(userId, recipient, contentType = 'contenu') {
    return this.addNotification(userId, {
      message: `📧 ${contentType} envoyé par email à ${recipient}`,
      type: 'success',
      category: 'action',
      metadata: { action: 'email', recipient, contentType }
    });
  }

  // Notification de suppression
  async addDeleteNotification(userId, itemName, itemType = 'élément') {
    return this.addNotification(userId, {
      message: `🗑️ ${itemType} "${itemName}" supprimé avec succès`,
      type: 'success',
      category: 'action',
      metadata: { action: 'delete', itemName, itemType }
    });
  }

  // Notification d'annulation
  async addCancelNotification(userId, operationType, itemName = '') {
    return this.addNotification(userId, {
      message: `⚠️ ${operationType} annulé${itemName ? ` pour "${itemName}"` : ''}`,
      type: 'warning',
      category: 'action',
      metadata: { action: 'cancel', operationType, itemName }
    });
  }

  // Notification d'erreur d'action
  async addActionErrorNotification(userId, actionType, errorMessage = 'Erreur inconnue') {
    return this.addNotification(userId, {
      message: `❌ Erreur lors de ${actionType}: ${errorMessage}`,
      type: 'error',
      category: 'action',
      metadata: { action: 'error', actionType, errorMessage }
    });
  }

  /**
   * Supprimer les notifications de progression pour une réunion donnée
   * @param {string} userId - ID de l'utilisateur
   * @param {string} meetingId - ID de la réunion
   * @param {string} category - Catégorie des notifications ('transcription' ou 'summary')
   * @returns {Promise<void>}
   */
  async removeProgressNotifications(userId, meetingId, category) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('type', '==', 'progress'),
        where('category', '==', category)
      );

      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(q, async (snapshot) => {
          try {
            const notificationsToDelete = snapshot.docs.filter(docSnapshot => {
              const data = docSnapshot.data();
              return data.metadata?.meetingId === meetingId;
            });

            const deletePromises = notificationsToDelete.map(docSnapshot =>
              updateDoc(docSnapshot.ref, {
                deleted: true,
                updatedAt: serverTimestamp()
              })
            );

            await Promise.all(deletePromises);
            unsubscribe();
            resolve();
          } catch (error) {
            unsubscribe();
            reject(error);
          }
        }, reject);
      });
    } catch (error) {

      throw new Error('Impossible de supprimer les notifications de progression');
    }
  }
}

// Instance singleton
const notificationService = new NotificationService();
export default notificationService; 